import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { AiOutlineMore } from "react-icons/ai";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";
import SearchableSelect from "../../components/selectForm";
import { useCheckinRegistrationRequests } from "../../hooks/useCheckinRegistration";
import { checkinV2Call } from "../../api/checkinV2";
import DeviceManagerTabsHeader from "./DeviceManagerTabsHeader";
import CheckinRegistrationTable from "./checkinRegistrationTable";
import DeleteDialog from "../../components/dialog/delete";
import FuelActionModal from "../fuelAllowance/FuelActionModal";
import iconBack from "../../assets/images/quailai.svg";

// Chấm công luồng mới - Bước 1: đơn đổi thiết bị / địa điểm chấm công (HCNS duyệt)
const StatusData = [
  { key: "PENDING", value: "Đang duyệt" },
  { key: "APPROVED", value: "Đã duyệt" },
  { key: "REJECTED", value: "Không duyệt" },
];

const headers = [
  { label: "Mã NV", key: "code", width: "7%" },
  { label: "Tên NV", key: "name", width: "11%" },
  { label: "Trạng thái", key: "status", width: "12%" },
  { label: "Lý do", key: "reason", width: "15%" },
  { label: "Thiết bị (cũ → mới)", key: "device", width: "18%" },
  { label: "Địa điểm (cũ → mới)", key: "place", width: "17%" },
  { label: "Ngày tạo", key: "createdAt", width: "9%" },
  { label: "Người xử lý", key: "handler", width: "11%" },
];

const TableCheckinRegistrationRequest = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);
  const debouncedSearchTerm = useDebounce(search, 500);
  const onChangeSearch = useCallback((e) => setSearch(e.target.value), []);

  const {
    items,
    refetch,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useCheckinRegistrationRequests(debouncedSearchTerm, status);

  // Popup: nhập lý do không duyệt / xác nhận duyệt chuyển máy / xác nhận hoàn tác đơn
  const [rejectRow, setRejectRow] = useState(null);
  const [transferRow, setTransferRow] = useState(null);
  const [revertRow, setRevertRow] = useState(null);

  // Trả true nếu cập nhật thành công (để đóng popup), lỗi thì giữ popup mở
  const submitStatus = async (nextStatus, row, adminNote = "") => {
    const toastId = toast.loading("Đang cập nhập trạng thái ...");
    let ok = false;
    try {
      await checkinV2Call.handleRequest(row.id, {
        status: nextStatus,
        ...(adminNote ? { adminNote } : {}),
      });
      await refetch();
      toast.success("Cập nhập trạng thái thành công");
      ok = true;
    } catch (error) {
      console.error("Error:", error);
    }
    toast.dismiss(toastId);
    return ok;
  };

  const onApprove = (row) => {
    if (row.deviceOwner) {
      setTransferRow(row);
      return;
    }
    submitStatus("APPROVED", row);
  };

  const onRevert = async () => {
    if (!revertRow) return;
    const toastId = toast.loading("Đang hoàn tác đơn ...");
    try {
      await checkinV2Call.revertRequest(revertRow.id);
      await refetch();
      setRevertRow(null);
      toast.success("Đã hoàn tác đơn, đơn chuyển về Đang duyệt");
    } catch (error) {
      console.error("Error:", error);
    }
    toast.dismiss(toastId);
  };

  const renderCell = (key, row) => {
    if (key !== "status") return <Text>{row[key]}</Text>;

    if (row.status === "APPROVED") {
      return <Text color={"var(--color-status-employ)"}>Đã duyệt</Text>;
    }
    if (row.status === "REJECTED") {
      return (
        <Flex alignItems={"center"} gap={"4px"}>
          <Text color={"var(--text-red-employee)"}>Không duyệt</Text>
          <Menu>
            <MenuButton
              _hover={{ backgroundColor: "none" }}
              _active={{ backgroundColor: "none" }}
              background="none"
              as={Button}
              size={"sm"}
              px={"4px"}
            >
              <AiOutlineMore fontSize={"20px"} />
            </MenuButton>
            <MenuList
              fontSize={{ base: "14px" }}
              fontWeight={500}
              lineHeight={"140%"}
              color={"var(--color-info-employee)"}
            >
              <MenuItem gap={"16px"} onClick={() => setRevertRow(row)}>
                <Image src={iconBack} /> Hoàn tác đơn
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      );
    }
    if (row.status !== "PENDING") return <Text>-</Text>;

    return (
      <Flex gap={"8px"} w={"100%"} flexWrap={"wrap"}>
        <Button
          bg={"var(--linear-gradient-employee)"}
          color={"var(--color-main)"}
          fontSize={"12px"}
          fontWeight={"600"}
          _hover={{ filter: "brightness(90%)" }}
          onClick={() => onApprove(row)}
          h={"28px"}
          minH={"28px"}
          w={"90px"}
        >
          Duyệt
        </Button>
        <Button
          bg={"var(--color-main)"}
          color={"var(--color-option-employee-hover)"}
          border={"1px solid var(--color-option-employee-hover)"}
          fontSize={"12px"}
          fontWeight={"600"}
          _hover={{ filter: "brightness(90%)" }}
          onClick={() => setRejectRow(row)}
          h={"28px"}
          minH={"28px"}
          w={"90px"}
        >
          Không duyệt
        </Button>
      </Flex>
    );
  };

  return (
    <>
      <DeviceManagerTabsHeader onRefreshCurrentTab={() => refetch()} />

      <Box mt={{ base: "23px" }}>
        <Flex
          backgroundColor="var(--color-main)"
          borderRadius={{ base: "12px" }}
          flexDirection={"column"}
        >
          <Flex
            p={"16px 16px 12px 16px"}
            gap={{ base: "16px 32px" }}
            alignItems={"center"}
            flexWrap={"wrap"}
          >
            <InputGroup
              maxW="42vw"
              minW={"42vw"}
              borderRadius={{ base: "8px" }}
              w={{ base: "100%" }}
            >
              <Input
                placeholder="Tên NV, mã NV ..."
                type="text"
                borderRadius={{ base: "8px" }}
                border={{ base: "1px solid var(--color-boder)" }}
                backgroundColor={"var(--fill-avatar)"}
                onChange={(e) => {
                  onChangeSearch(e);
                  setCurrentPage(1);
                }}
              />
              <InputRightElement
                borderTopRightRadius={"8px"}
                borderBottomRightRadius={"8px"}
                pointerEvents="none"
                backgroundImage="var(--color-button)"
              >
                <SearchIcon color="var(--color-main)" />
              </InputRightElement>
            </InputGroup>
            <SearchableSelect
              isShowSearch={false}
              options={StatusData}
              initialSelectedOption={{ key: 0, value: "Chọn trạng thái" }}
              setValue={(v) => {
                setStatus(v);
                setCurrentPage(1);
              }}
            />
          </Flex>

          <CheckinRegistrationTable
            headers={headers}
            rows={items}
            renderCell={renderCell}
            isLoaded={!isLoading && !isRefetching}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(p) => setCurrentPage(p.selected + 1)}
            startEntry={startEntry}
            endEntry={endEntry}
            entries={entries}
          />
        </Flex>
      </Box>

      <FuelActionModal
        isOpen={!!rejectRow}
        onClose={() => setRejectRow(null)}
        title={"Không duyệt đơn đổi thiết bị/địa điểm"}
        fields={[
          {
            label: "Nhân viên",
            value: rejectRow ? `${rejectRow.code} - ${rejectRow.name}` : "",
          },
          { label: "Thiết bị (cũ → mới)", value: rejectRow?.device || "" },
          { label: "Địa điểm (cũ → mới)", value: rejectRow?.place || "" },
        ]}
        noteLabel={"Lý do không duyệt"}
        notePlaceholder={"Nhập lý do không duyệt (không bắt buộc)"}
        submitText={"Không duyệt"}
        onSubmit={async (note) => {
          if (await submitStatus("REJECTED", rejectRow, note)) {
            setRejectRow(null);
          }
        }}
      />

      <DeleteDialog
        isOpen={!!transferRow}
        onClose={() => setTransferRow(null)}
        title={"Duyệt chuyển thiết bị"}
        description={
          transferRow
            ? `Thiết bị trong đơn đang đăng ký cho nhân viên ${transferRow.deviceOwner}. Duyệt sẽ chuyển thiết bị sang ${transferRow.code} - ${transferRow.name}, nhân viên ${transferRow.deviceOwner} phải đăng ký lại. Bạn có chắc chắn muốn duyệt không?`
            : ""
        }
        action={async () => {
          if (await submitStatus("APPROVED", transferRow)) {
            setTransferRow(null);
          }
        }}
        textDelete={"Duyệt"}
        isDelete={false}
      />

      <DeleteDialog
        isOpen={!!revertRow}
        onClose={() => setRevertRow(null)}
        title={"Hoàn tác đơn"}
        description={
          revertRow
            ? `Bạn có chắc chắn muốn đưa đơn của nhân viên ${revertRow.code} - ${revertRow.name} về trạng thái Đang duyệt không?`
            : ""
        }
        action={onRevert}
        textDelete={"Hoàn tác"}
        isDelete={false}
      />
    </>
  );
};

export default TableCheckinRegistrationRequest;
